import * as React from "react";
import { useState } from "react";
import styled from "styled-components";
import { generic, mobile } from "../../../../ui/_themes/constants";
import { NumPad } from "../../../../ui/NumPad";
import { numberToCents, stringifyAmount } from "./Autoplay.utils";
import {
  IAutoplaySetting,
  IAutoplaySettingAttribute
} from "./AutoplaySettings";

export interface IAttribute {
  disabled: boolean;
  setting: IAutoplaySetting;
  onChange: (setting: IAutoplaySetting) => void;
}

const DEFAULT_ATTRIBUTE: IAutoplaySettingAttribute = {
  label: "",
  value: null,
  required: false
};

export function Attribute({
  setting,
  onChange,
  disabled: isDisabled
}: IAttribute) {
  const { text, attributes } = setting;
  const { label, value, required } = attributes || DEFAULT_ATTRIBUTE;
  const valueInCurrency = stringifyAmount(value);

  const [numPadVisible, setNumPadVisible] = useState(false);

  const onClickOnValue = () => setNumPadVisible(true);
  const onCancelNumPad = () => setNumPadVisible(false);

  const onValueFromNumPad = (v: string) => {
    const result: number = v === "" ? NaN : parseInt(v, 10);
    const inCents: number = numberToCents(result);
    const value = isNaN(inCents) ? null : inCents;

    onChange({
      ...setting,
      attributes: {
        label,
        value,
        required
      }
    });

    setNumPadVisible(false);
  };

  const isEmpty = valueInCurrency === "";
  const isRequiredWithError = isEmpty && required;

  // decided which component should be rendered
  const Value = isDisabled
    ? ValueDisabled
    : isRequiredWithError
    ? ValueError
    : isEmpty
    ? ValueError
    : ValueSuccess;

  const onClickHandler = isDisabled ? null : onClickOnValue;

  return (
    <>
      <Wrapper>
        <Value label={label} onClick={onClickHandler}>
          {valueInCurrency}
        </Value>
      </Wrapper>
      {numPadVisible && (
        <NumPad
          maxChars={13}
          alt={text}
          currency={label}
          value={valueInCurrency}
          onDone={onValueFromNumPad}
          onCancel={onCancelNumPad}
        />
      )}
    </>
  );
}

const Wrapper = styled.div`
  position: relative;
  padding: 0.6em 0;
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  padding-top: 1em;
`;

const ValueEnabled = styled.div`
  user-select: none;
  flex: 1 0;
  pointer-events: all;
  font-size: ${mobile.fontSize.m};
  font-weight: 600;
  padding: 0.4em 0;
  color: white;
  border-bottom: 0.06em solid ${generic.autoplaySettings.border.color};
  position: relative;
  min-height: 2em;
  &:after {
    position: absolute;
    content: "${props => props.label}";
    font-size: 1em;
    right: 0;
    color: white;
    font-weight: normal;
  }
`;

export const ValueDisabled = styled(ValueEnabled)`
  color: ${generic.autoplaySettings.text.disabled};
  border-bottom-color: ${generic.autoplaySettings.border.disabled};
  &:after {
    color: ${generic.autoplaySettings.text.disabled};
  }
`;

export const ValueSuccess = styled(ValueEnabled)`
  color: ${generic.autoplaySettings.text.active};
  border-bottom-color: ${generic.autoplaySettings.border.valid};
`;

export const ValueError = styled(ValueEnabled)`
  color: ${generic.autoplaySettings.text.invalid};
  border-bottom-color: ${generic.autoplaySettings.border.invalid};
`;
